import {
  ICurrency,
  ICurrencyTableColumns,
  INewCurrencyQuery,
  useCreateCurrencyMutation,
  useDeleteCurrencyMutation,
  useEditCurrencyMutation,
} from '@entities/currency';
import { ModalDeleteEntity } from '@features/modal-entity-delete';
import { ModalEditEntity } from '@features/modal-entity-edit';
import { ModalNewEntity } from '@features/modal-entity-new';
import { ModalViewEntity } from '@features/modal-entity-view';
import { ITableActionsConfig, TableActionBtns } from '@features/table-action-btns';

interface IProps {
  refetch?: () => void;
}

export const useTableSettings = ({ refetch }: IProps) => {
  const [deleteCurrency] = useDeleteCurrencyMutation();

  // THead settings
  const columnTitles: ICurrencyTableColumns = {
    id: 'ID',
    code: 'Code',
    title: 'Title',
    created_at: 'Created At',
    updated_at: 'Updated At',
    symbol: 'Symbol',
    actions: 'Actions',
  };

  // TBody settings
  const renderRow = (key: keyof ICurrencyTableColumns, currency: ICurrency) => {
    switch (key) {
      case 'actions': {
        const excludeKeys: (keyof ICurrency)[] = ['id', 'created_at', 'updated_at', 'deleted_at'];

        const actionBtnsConfig: ITableActionsConfig = {
          edit: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalEditEntity
                title={`Edit Currency ${currency.code}`}
                entity={currency}
                excludeKeys={excludeKeys}
                useEditEntityMutation={useEditCurrencyMutation}
                refetch={refetch}
                omitUnchangedValues={true}
              />
            ),
            popupSize: 'sm',
          },
          view: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalViewEntity
                excludeKeys={['deleted_at']}
                entity={currency}
                title={`View Currency ${currency.code}`}
              />
            ),
            popupSize: 'md',
          },
          delete: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalDeleteEntity
                id={currency.id}
                entityName={`${currency.code} (${currency.title || 'No title'})`}
                message='Do you want to delete this currency?'
                deleteEntity={deleteCurrency}
                refetch={refetch}
              />
            ),
            popupSize: 'sm',
          },
        };

        return <TableActionBtns config={actionBtnsConfig} />;
      }

      default:
        return currency[key];
    }
  };

  // New merchant settings
  const newCurrencyKeys: (keyof INewCurrencyQuery)[] = ['code', 'title', 'symbol'];
  const NewCurrency = () => (
    <ModalNewEntity
      title='New Currency'
      entityKeys={newCurrencyKeys}
      useCreateEntityMutation={useCreateCurrencyMutation}
      refetch={refetch}
    />
  );

  return {
    columnTitles,
    renderRow,
    NewCurrency,
  };
};
